import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Box, IconButton, Collapse } from '@material-ui/core';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import { useTranslation } from '../hooks/useTranslation';
import FileDownload from './FileDownload';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0.5),
    minWidth: 150,
  },
  section: {
    marginBottom: theme.spacing(0.5),
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.25),
    color: theme.palette.text.secondary,
  },
  emptyState: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  collapsibleSection: {
    position: 'relative',
  },
  expandButton: {
    padding: theme.spacing(0.5),
    marginTop: theme.spacing(0.25),
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'block',
    color: 'var(--ikaro-orange-primary)',
    minWidth: 'auto',
    width: 'auto',
    height: 'auto',
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: 'rgba(255, 152, 0, 0.1)',
      color: 'var(--ikaro-orange-dark)',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  },
  collapsedContent: {
    maxHeight: '80px',
    overflow: 'hidden',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '20px',
      background: 'linear-gradient(transparent, rgba(255,255,255,0.9))',
      pointerEvents: 'none',
    },
  },
  itemCount: {
    fontSize: '0.65rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginLeft: theme.spacing(0.5),
  },
}));

// Número máximo de itens visíveis quando contraído
const MAX_VISIBLE_ITEMS = 3;

const ContestacaoInputDisplay = ({ inputPayload }) => {
  const classes = useStyles();
  const { t } = useTranslation('jobs');
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);

  useEffect(() => {
    if (inputPayload?.files?.subsidios) {
      const totalItems = inputPayload.files.subsidios.length + (inputPayload.files.peticao_inicial ? 1 : 0);
      setShouldShowToggle(totalItems > MAX_VISIBLE_ITEMS);
    } else {
      setShouldShowToggle(false);
    }
  }, [inputPayload]);

  if (!inputPayload || !inputPayload.files) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.emptyState}>
          {t('contestacao.inputDisplay.noInputFiles')}
        </Typography>
      </Box>
    );
  }

  const { files } = inputPayload;
  const subsidiosCount = files.subsidios ? files.subsidios.length : 0;

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const renderContent = () => (
    <>
      {/* Petição Inicial */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          {t('contestacaoCreate.initialPetition')}
        </Typography>
        {files.peticao_inicial ? (
          <FileDownload 
            file={files.peticao_inicial}
            html_preview_token={files.peticao_inicial.html_preview_token}
          />
        ) : (
          <Typography className={classes.emptyState}>
            {t('contestacao.inputDisplay.noFiles')}
          </Typography>
        )}
      </Box>

      {/* Subsídios */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          {t('contestacaoCreate.subsidies')}
          {subsidiosCount > 0 && (
            <span className={classes.itemCount}>
              ({subsidiosCount} {subsidiosCount === 1 ? t('contestacao.inputDisplay.file') : t('contestacao.inputDisplay.files')})
            </span>
          )}
        </Typography>
        {files.subsidios && files.subsidios.length > 0 ? (
          files.subsidios.map((file, index) => (
            <FileDownload 
              key={`subsidio-${file.token || file.filename || index}`}
              file={file}
              html_preview_token={file.html_preview_token}
            />
          ))
        ) : (
          <Typography className={classes.emptyState}>
            {t('contestacao.inputDisplay.noFiles')}
          </Typography>
        )}
      </Box>
    </>
  );

  return (
    <Box className={classes.container}>
      {shouldShowToggle ? (
        <Box className={classes.collapsibleSection}>
          {isExpanded ? (
            <Collapse in={isExpanded} timeout="auto">
              {renderContent()}
            </Collapse>
          ) : (
            <Box className={classes.collapsedContent}>
              {renderContent()}
            </Box>
          )}
          
          <IconButton
            className={classes.expandButton}
            onClick={handleToggleExpand}
            size="small"
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      ) : (
        renderContent()
      )}
    </Box>
  );
};

export default ContestacaoInputDisplay; 